import React, { useEffect, useRef, useState } from 'react';
import { api, type MarkGroup, type MarkRequirements, type PlatformAgreementStatus, type SigningDocument } from '../api';
import { TYPE, useTheme } from '../theme';
import { Button, Card, Field, Input, Select, Switch, Textarea } from '../components/ui';

/**
 * P2/P3/KO1/KO2 · Under antagningen: markens krav, avtalet per grupp (textmall eller eget
 * kontrakt i Word/PDF) och markägarens avtal med Qvenari.
 */
export function ContractSettings({ markId, canEdit, isOwner }: { markId: string; canEdit: boolean; isOwner: boolean }) {
  const T = useTheme();
  const [open, setOpen] = useState(false);
  if (!canEdit && !isOwner) return null;
  return (
    <Card style={{ marginTop: 20 }} padding={0}>
      <button aria-expanded={open} onClick={() => setOpen(v => !v)} data-testid="contract-settings-toggle"
        style={{ width: '100%', textAlign: 'left', background: 'none', border: 'none', padding: '16px', cursor: 'pointer', color: T.text, fontFamily: 'inherit', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, minHeight: 44 }}>
        <span>
          <span style={{ ...TYPE.title, display: 'block' }}>Avtal och krav</span>
          <span style={{ ...TYPE.small, color: T.textMuted }}>Krav vid antagning, avtal per grupp och ert avtal med Qvenari</span>
        </span>
        <span style={{ color: T.accentText, fontWeight: 700, fontSize: 14 }}>{open ? 'Stäng' : 'Öppna'}</span>
      </button>
      {open && (
        <div style={{ padding: '0 16px 18px', display: 'flex', flexDirection: 'column', gap: 24 }}>
          {canEdit && <RequirementsEditor markId={markId} />}
          {canEdit && <GroupContracts markId={markId} />}
          {isOwner && <PlatformAgreement markId={markId} />}
        </div>
      )}
    </Card>
  );
}

const sub = (T: ReturnType<typeof useTheme>): React.CSSProperties => ({ ...TYPE.label, color: T.textMuted, marginBottom: 10 });

function RequirementsEditor({ markId }: { markId: string }) {
  const T = useTheme();
  const [req, setReq] = useState<Partial<MarkRequirements> | null>(null);
  const [saved, setSaved] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.admissionRequirements(markId).then(r => setReq(r ?? { requiresExam: true })).catch(() => setReq({ requiresExam: true })); }, [markId]);
  if (!req) return null;
  const set = (k: keyof MarkRequirements, v: unknown) => { setSaved(''); setReq(r => ({ ...r, [k]: v })); };
  const toggles: [keyof MarkRequirements, string][] = [
    ['requiresExam', 'Jägarexamen'], ['requiresWeaponLicense', 'Giltig vapenlicens'], ['requiresShootingTest', 'Skjutprov / skyttebevis'],
    ['requiresInsurance', 'Jaktförsäkring'], ['requiresDog', 'Hund eller hundförare'], ['afterhuntRequired', 'Eftersöksutbildning'],
  ];
  const save = async () => {
    setBusy(true);
    try {
      await api.setAdmissionRequirements(markId, {
        requiresExam: req.requiresExam !== false, requiresWeaponLicense: !!req.requiresWeaponLicense, requiresShootingTest: !!req.requiresShootingTest,
        requiresInsurance: !!req.requiresInsurance, requiresDog: !!req.requiresDog, afterhuntRequired: !!req.afterhuntRequired,
        minHunterYears: Number(req.minHunterYears) || 0,
      });
      setSaved('Sparat');
    } catch (e: any) { setSaved(e.message); }
    setBusy(false);
  };
  return (
    <div data-testid="admission-requirements-editor">
      <div style={sub(T)}>Krav vid antagning</div>
      <div style={{ ...TYPE.small, color: T.textSub, marginBottom: 10 }}>Kontrolleras mot jägarens profil när hen köpt en annons. Godkännaren beslutar alltid själv.</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 8 }}>
        {toggles.map(([k, label]) => (
          <Switch key={k} checked={k === 'requiresExam' ? req[k] !== false : !!req[k]} onChange={v => set(k, v)} label={label}
            style={{ justifyContent: 'space-between', color: T.text, fontSize: 15 }} />
        ))}
      </div>
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap', marginTop: 8 }}>
        <Field label="Minst antal år sedan jägarexamen" style={{ maxWidth: 260 }}>
          <Input type="number" min={0} max={60} value={req.minHunterYears ?? 0} onChange={e => set('minHunterYears', e.target.value)} />
        </Field>
        <Button loading={busy} onClick={save}>Spara krav</Button>
        {saved && <span role="status" style={{ ...TYPE.small, color: saved === 'Sparat' ? T.ok : T.danger }}>{saved}</span>}
      </div>
    </div>
  );
}

const PLACEHOLDERS = '{{jagare}} {{epost}} {{mark}} {{annons}} {{period}} {{belopp}} {{grupp}} {{giltighet}} {{datum}}';

function GroupContracts({ markId }: { markId: string }) {
  const T = useTheme();
  const [groups, setGroups] = useState<MarkGroup[]>([]);
  const [docs, setDocs] = useState<SigningDocument[]>([]);
  const [groupId, setGroupId] = useState('');
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  const load = () => api.markSigningDocuments(markId).then(setDocs).catch(e => setMsg(e.message));
  useEffect(() => { api.markGroups(markId).then(setGroups).catch(() => {}); void load(); }, [markId]); // eslint-disable-line react-hooks/exhaustive-deps

  const own = docs.find(d => (d.groupId ?? '') === groupId) ?? null;
  const inherited = !own && groupId ? docs.find(d => !d.groupId) ?? null : null;
  const current = own ?? inherited;
  const groupName = groupId ? groups.find(g => g.id === groupId)?.name ?? 'gruppen' : 'alla grupper';

  const startEdit = () => {
    setMsg('');
    setTitle(own?.kind === 'template' ? own.title : `Avtal – ${groupId ? groupName : 'jakt'}`);
    setBody(own?.kind === 'template' ? own.bodyText ?? '' : '');
    setEditing(true);
  };
  const saveTemplate = async () => {
    setBusy('save'); setMsg('');
    try { await api.saveSigningTemplate(markId, groupId || null, title, body); setEditing(false); setMsg('Mallen är sparad'); await load(); }
    catch (e: any) { setMsg(e.message); }
    setBusy('');
  };
  const upload = async (file: File) => {
    setBusy('upload'); setMsg('');
    try {
      await api.uploadSigningDocument({ scope: 'mark', markId, groupId: groupId || null, title: file.name.replace(/\.(pdf|docx)$/i, ''), file });
      setMsg('Kontraktet är uppladdat och används från nästa antagning'); await load();
    } catch (e: any) { setMsg(e.message); }
    setBusy('');
    if (fileRef.current) fileRef.current.value = '';
  };
  const remove = async () => {
    if (!own || !confirm(`Ta bort avtalet för ${groupName}? ${groupId ? 'Markens avtal för alla grupper' : 'Qvenaris standardmall'} används då i stället.`)) return;
    setBusy('remove');
    try { await api.deactivateSigningDocument(own.id); await load(); } catch (e: any) { setMsg(e.message); }
    setBusy('');
  };

  return (
    <div data-testid="group-contracts">
      <div style={sub(T)}>Avtal med jägaren</div>
      <div style={{ ...TYPE.small, color: T.textSub, marginBottom: 10 }}>
        Skickas med mejl när ni godkänner en jägare. Jägaren signerar med namn och fingerritad signatur och läggs sedan i gruppen.
        Ett eget kontrakt (Word .docx eller PDF) används i stället för mallen.
      </div>
      <Field label="Gäller" style={{ maxWidth: 360, marginBottom: 12 }}>
        <Select value={groupId} onChange={e => { setGroupId(e.target.value); setEditing(false); setMsg(''); }}>
          <option value="">Alla grupper</option>
          {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
        </Select>
      </Field>
      <div style={{ ...TYPE.small, color: T.text, marginBottom: 10 }} data-testid="current-contract">
        {current
          ? <>Används: <b>{current.title}</b> ({current.kind === 'file' ? current.fileName : 'textmall'}){inherited ? ' – markens avtal för alla grupper' : ''}</>
          : <>Används: Qvenaris standardmall (parter, period, pris och grundregler).</>}
        {own?.kind === 'file' && <button onClick={() => api.openSigningFile('document', own.id).catch(e => setMsg(e.message))}
          style={{ background: 'none', border: 'none', color: T.accentText, fontWeight: 700, cursor: 'pointer', fontSize: 14, marginLeft: 6, minHeight: 44, fontFamily: 'inherit' }}>Öppna</button>}
      </div>
      {editing ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <Field label="Rubrik"><Input value={title} onChange={e => setTitle(e.target.value)} /></Field>
          <Field label="Avtalstext" hint={`Fält som fylls i: ${PLACEHOLDERS}`}>
            <Textarea rows={12} value={body} onChange={e => setBody(e.target.value)} placeholder={'Upplåtare: {{mark}}\nJägare: {{jagare}}\n…'} data-testid="template-body" />
          </Field>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Button loading={busy === 'save'} onClick={saveTemplate}>Spara mallen</Button>
            <Button variant="ghost" onClick={() => setEditing(false)}>Avbryt</Button>
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Button variant="secondary" onClick={startEdit}>{own?.kind === 'template' ? 'Ändra mallen' : 'Skriv egen mall'}</Button>
          <Button variant="secondary" loading={busy === 'upload'} onClick={() => fileRef.current?.click()}>Ladda upp kontrakt (Word/PDF)</Button>
          {own && <Button variant="ghost" loading={busy === 'remove'} onClick={remove}>Ta bort</Button>}
          <input ref={fileRef} type="file" accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document" hidden
            data-testid="contract-upload" onChange={e => { const f = e.target.files?.[0]; if (f) void upload(f); }} />
        </div>
      )}
      {msg && <div role="status" style={{ ...TYPE.small, color: T.textSub, marginTop: 8 }}>{msg}</div>}
    </div>
  );
}

function PlatformAgreement({ markId }: { markId: string }) {
  const T = useTheme();
  const [st, setSt] = useState<PlatformAgreementStatus | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  useEffect(() => { api.platformAgreement(markId).then(setSt).catch(e => setErr(e.message)); }, [markId]);
  const sign = async () => {
    setBusy(true); setErr('');
    try {
      const r = st?.signingToken ? st : await api.requestPlatformAgreement(markId);
      if (r.signingToken) window.location.href = `/avtal/${encodeURIComponent(r.signingToken)}`;
      else setSt(r);
    } catch (e: any) { setErr(e.message); }
    setBusy(false);
  };
  const signed = st?.request?.status === 'signed';
  return (
    <div data-testid="platform-agreement">
      <div style={sub(T)}>Avtal med Qvenari</div>
      {!st ? <div style={{ ...TYPE.small, color: T.textMuted }}>{err || 'Hämtar …'}</div>
        : !st.document ? <div style={{ ...TYPE.small, color: T.textSub }}>Qvenari har inte lagt upp markägaravtalet ännu.</div>
        : signed ? (
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ ...TYPE.small, color: T.text }}>”{st.document.title}” signerat {new Date(Number(st.request!.signedAt)).toLocaleDateString('sv-SE')} av {st.request!.signedName}.</span>
            <Button variant="secondary" onClick={() => api.openSigningFile('signed', st.request!.id).catch(e => setErr(e.message))}>Öppna (PDF)</Button>
          </div>
        ) : (
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ ...TYPE.small, color: T.text }}>”{st.document.title}” – {st.request?.status === 'sent' ? 'väntar på din signatur' : 'inte signerat'}.</span>
            <Button loading={busy} onClick={sign} data-testid="platform-sign">Läs och signera</Button>
          </div>
        )}
      {err && st && <div role="alert" style={{ ...TYPE.small, color: T.danger, marginTop: 6 }}>{err}</div>}
    </div>
  );
}
